import { useQuery } from '@tanstack/react-query';
import { LoanApi } from '@apply/api';
import { LoanGradeType } from '@apply/constants';
import { applicationInsights } from '../../../../../ApplicationInsights';
import { LoanType } from '../../../../common/constants/LoanType';
import { LoanStatusType } from '../../../../common/constants/LoanStatusType';
import { InterestAcrualType } from '../../../../common/constants/InterestAcrualType';
import { LoanFile } from '../../../../common/components/v2/LoanFilesTable/constants/loanFile';

const loanApi = new LoanApi.LoanApi(applicationInsights);

/**
 * Which loan the modal opens; give one. loanId edits an existing loan; the others add a new one: of a loan type, as a
 * sub note of a master note, or for a B-Client (whose borrower is then locked).
 */
export interface StandardLoanSource {
    loanId?: number;
    loanTypeId?: LoanType;
    masterNoteId?: number;
    bClientId?: number;
}

/**
 * A standard loan, or a new-loan template, as the loan endpoints return it: only the fields the modal reads. (The API's
 * IStandardLoan type is missing several of these.)
 */
export interface StandardLoanDetails {
    /** 0 or missing for a new loan. */
    id?: number;
    /** Missing on some new-loan templates; the modal falls back to the loanTypeId it was opened with. */
    loanTypeId?: LoanType;
    statusId?: LoanStatusType;
    approvalDate?: string | null;
    noteDate?: string | null;
    maturityDate?: string | null;
    initialTermMonths?: number | null;
    contractualExtension1Months?: number | null;
    contractualExtension2Months?: number | null;
    bfiNumber?: string | null;
    loanNumber?: string | number | null;
    pointOfContactId?: string | null;
    underwriterId?: string | null;
    callCodeType?: number | null;
    inForbearance?: boolean | null;
    forbearanceAgreementTerms?: string | null;
    masterNoteBfiNumber?: string | null;
    borrowerEntityId?: number | null;
    brokerId?: number | null;
    brokerAgreementId?: number | null;
    interestTypeId?: number | null;
    interestRate?: number | null;
    interestAcrualTypeId?: InterestAcrualType | null;
    interestRateFloor?: number | null;
    interestRateCeiling?: number | null;
    reserveRate?: number | null;
    grade?: LoanGradeType | null;
    entireStructure?: boolean | null;
    underwritingFee?: number | null;
    originationFee?: number | null;
    brokerShare?: number | null;
    brokerAdditional?: number | null;
    liquidity?: number | null;
    commitment?: number | null;
    promissoryNote?: number | null;
    bridgeFunded?: number | null;
    files?: LoanFile[] | null;
    properties?:
        | {
              id?: number | null;
              bfiNumberSuffix?: number | null;
              commitment?: number | null;
              originationFee?: number | null;
          }[]
        | null;
}

/** True when the source names a loan to open (the modal can also open empty). */
const hasSource = (source: StandardLoanSource) =>
    !!(source.loanId || source.masterNoteId || source.bClientId || source.loanTypeId);

/**
 * The loan the modal opens with, from whichever of the source's ids is given (the same endpoints, and the same order,
 * as EditStdLoanModal). openId should change on every open, so each open fetches its own copy and loads it into the
 * steps once; nothing refetches it in the background, which would overwrite what's been entered since.
 */
export const useStandardLoanQuery = (source: StandardLoanSource, openId: number, isOpen: boolean) =>
    useQuery({
        queryKey: ['standardLoan', source.loanId, source.masterNoteId, source.bClientId, source.loanTypeId, openId],
        queryFn: async (): Promise<StandardLoanDetails> => {
            const response = source.loanId
                ? await loanApi.getStandardLoanById(source.loanId)
                : source.masterNoteId
                  ? await loanApi.createSubnote(source.masterNoteId)
                  : // The API takes @apply/constants' LoanType, a separate enum with the same values as admin's
                    await loanApi.createStandardLoan(source.loanTypeId as number | undefined, source.bClientId);
            if (response.statusCode === 200) return response.result as unknown as StandardLoanDetails;

            throw new Error('Failed to load the loan.');
        },
        enabled: isOpen && hasSource(source),
        staleTime: Infinity,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        retry: false,
    });
